import { ReactElement, useState } from 'react';
import { UIText, UITextProps, UIToast, UITagProps, UITagGroup } from '@/ui';
import { moonlight, opacity } from '@/ui/styles';
import { useTasksTranslation } from '../hooks';

export type TaskInfoItemProps = {
    title: string;
    titleProps?: UITextProps;
    rightTitle?: string | null;
    rightTitleProps?: UITextProps;
    text?: string | null;
    textProps?: UITextProps;
    description?: string | null;
    phone?: string | null;
    w3w?: string | null;
    listNumber?: number;
    onClick?: () => void;
    onHoverText?: string | null;
    copyToClipboardText?: string | null;
    copiedToClipboardNotificationText?: string | null;
    tags?: UITagProps[];
};
export function TaskInfoItem(props: TaskInfoItemProps): ReactElement {
    const {
        title,
        titleProps,
        rightTitle,
        rightTitleProps,
        text,
        textProps,
        phone,
        w3w,
        listNumber,
        onClick,
        onHoverText,
        copyToClipboardText,
        copiedToClipboardNotificationText,
        tags = []
    } = props;

    const { t } = useTasksTranslation();

    // Resolved in the render rather than as a default parameter: a default is
    // evaluated once, so it would not follow a language change.
    const copiedNotification =
        copiedToClipboardNotificationText === undefined
            ? t('components.task_info_item.copied_to_clipboard')
            : copiedToClipboardNotificationText;

    const [hover, setHover] = useState(false);
    const handleMouseEnter = () => setHover(true);
    const handleMouseLeave = () => setHover(false);

    const clickable = !!onClick || !!copyToClipboardText;

    function copyToClipboard(text: string): void {
        void navigator.clipboard.writeText(text);
        if (copiedNotification) {
            UIToast.success(copiedNotification);
        }
    }

    return (
        <div
            style={{
                marginLeft: 8,
                marginRight: 8
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    padding: 8,
                    gap: 8,
                    backgroundColor:
                        clickable && hover
                            ? moonlight + opacity(0.6)
                            : 'inherit',
                    cursor: clickable ? 'pointer' : 'default',
                    borderRadius: 8
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={() => {
                    if (clickable && onClick) {
                        onClick();
                    } else if (clickable && copyToClipboardText) {
                        copyToClipboard(copyToClipboardText);
                    }
                }}
            >
                {listNumber !== undefined && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <UIText>{`${listNumber}.`}</UIText>
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        width: '100%'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between'
                        }}
                    >
                        <UIText variant="small" type="default2" {...titleProps}>
                            {title}
                        </UIText>

                        {rightTitle && (
                            <UIText
                                variant="small"
                                type="default2"
                                {...rightTitleProps}
                            >
                                {rightTitle}
                            </UIText>
                        )}
                        {onHoverText && hover && (
                            <UIText variant="small" type="link">
                                {onHoverText}
                            </UIText>
                        )}
                        {copyToClipboardText &&
                            !rightTitle &&
                            !onHoverText &&
                            hover && (
                                <UIText variant="small" type="link">
                                    {t('shared.labels.copy')}
                                </UIText>
                            )}
                    </div>
                    {text && <UIText {...textProps}>{text}</UIText>}
                    {phone && (
                        <a
                            style={{ textDecoration: 'none', color: 'inherit' }}
                            href={`tel:${phone}`}
                        >
                            <UIText type="link">{phone}</UIText>
                        </a>
                    )}
                    {w3w && (
                        <UIText type="error">
                            {'/// '}
                            <UIText type="link">{w3w}</UIText>
                        </UIText>
                    )}
                    {tags.length > 0 && (
                        <div style={{ display: 'flex', paddingTop: 4 }}>
                            <UITagGroup tags={tags} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
